import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { scrollToId } from "@/components/site/SmoothScroll";

const ease = [0.22, 1, 0.36, 1];

const Badge = () => (
  <div className="absolute -left-6 -bottom-8 sm:-left-10 sm:-bottom-10 w-28 h-28 sm:w-36 sm:h-36 z-20" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 200 200" className="w-full h-full spin-slow">
      <defs><path id="c" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <circle cx="100" cy="100" r="98" fill="#FFFFFF" />
      <text fontFamily="JetBrains Mono" fontSize="15" letterSpacing="4" fill="#14083E" fontWeight="600">
        <textPath href="#c">IMG HEALTH • HEALTHCARE AGGREGATOR •</textPath>
      </text>
    </svg>
    <div className="absolute inset-0 grid place-items-center">
      <span className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-brand-gradient grid place-items-center text-white">
        <ArrowDownRight className="w-6 h-6" />
      </span>
    </div>
  </div>
);

const Visual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const py = useTransform(scrollY, [0, 700], [0, -80]);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative mx-auto w-full max-w-[520px] [perspective:1200px]" style={{ y: py }}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.div initial={{ clipPath: "inset(100% 0 0 0 round 240px 240px 28px 28px)" }}
          animate={{ clipPath: "inset(0% 0 0 0 round 240px 240px 28px 28px)" }} transition={{ duration: 1.4, ease, delay: 0.35 }}
          className="relative aspect-[4/5] overflow-hidden" data-testid="hero-main-photo">
          <motion.img src="/images/admission.jpg" alt="Doctor guiding a patient" className="h-full w-full object-cover"
            initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease, delay: 0.35 }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14083E]/70 via-transparent to-transparent" />
          <p className="absolute bottom-5 left-24 sm:left-32 right-5 text-white font-display text-base sm:text-lg font-semibold">From the admission desk to recovery at home.</p>
        </motion.div>
        <motion.div style={{ transform: "translateZ(80px)" }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease, delay: 1.1 }}
          className="absolute -right-3 sm:-right-10 top-10 w-36 sm:w-48 aspect-square rounded-[28px] overflow-hidden border-4 border-[#0B0424] shadow-2xl">
          <img src="/images/lab.jpg" alt="Diagnostic lab samples" className="h-full w-full object-cover" />
        </motion.div>
        <motion.div style={{ transform: "translateZ(120px)" }} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease, delay: 1.3 }}
          className="absolute -right-2 sm:-right-6 bottom-24 rounded-2xl bg-white/95 backdrop-blur px-4 py-3 shadow-2xl">
          <p className="font-mono-x text-[10px] uppercase tracking-[0.2em] text-[#c8106a]">Aggregated network</p>
          <p className="font-display font-bold text-[#14083E]">Hospitals · Labs · Insurers</p>
        </motion.div>
        <Badge />
      </motion.div>
    </motion.div>
  );
};

export default function Hero() {
  return (
    <section className="relative min-h-[100svh] bg-[#0B0424] text-white overflow-hidden grain" data-testid="hero-section">
      <div aria-hidden className="absolute -top-40 -right-40 h-[620px] w-[620px] rounded-full bg-[#E5157A]/40 blur-[140px]" />
      <div aria-hidden className="absolute bottom-[-200px] left-[-120px] h-[520px] w-[520px] rounded-full bg-[#2f8cff]/25 blur-[140px]" />
      <div aria-hidden className="absolute top-1/3 left-1/3 h-[300px] w-[300px] rounded-full bg-[#FF7A1F]/20 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-24 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
            className="font-mono-x text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[#ff8fbf] flex items-center gap-3" data-testid="hero-eyebrow">
            <span className="h-px w-10 bg-brand-gradient" /> An Amit Sadana Initiative
          </motion.p>
          <h1 className="mt-6 font-display font-extrabold tracking-[-0.035em] leading-[0.92] text-[3.1rem] sm:text-7xl lg:text-[6.4rem]" data-testid="hero-title">
            <MaskLines delay={0.2} lines={["Every path", "to better care,", { text: "in one place.", className: "text-brand-gradient" }]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease }}
            className="mt-8 max-w-xl text-base sm:text-lg text-white/80 leading-relaxed">
            IMG Health is an integrated healthcare aggregation and patient support company that connects patients with trusted hospitals, diagnostic laboratories, insurers, and home-care providers. We simplify the healthcare journey by guiding patients and their families at every step—from the initial enquiry and care coordination to treatment and final billing.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease }}
            className="mt-10 flex flex-wrap gap-3">
            <button data-testid="hero-explore-services-btn" onClick={() => scrollToId("services")}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-gradient px-7 py-4 font-semibold shadow-[0_20px_50px_-15px_rgba(229,21,122,0.8)] hover:scale-[1.03] active:scale-[0.98] transition-transform">
              See what we cover <ArrowDownRight className="w-5 h-5 group-hover:rotate-[-45deg] transition-transform duration-300" />
            </button>
            <Link to="/join/franchise" data-testid="hero-join-btn"
              className="group inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-4 font-semibold hover:bg-white hover:text-[#14083E] transition-colors duration-300">
              Grow with IMG Health <ArrowUpRight className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }}
            className="mt-12 font-mono-x text-[11px] uppercase tracking-[0.25em] text-white/70" data-testid="hero-regent-line">
            — A Regent Group Enterprise
          </motion.p>
        </div>
        <div className="lg:col-span-5 px-6 sm:px-10 lg:px-0"><Visual /></div>
      </div>
    </section>
  );
}
